import { uid } from '../../store/utils';

type ReportSectionKind =
  | 'custom'
  | 'narrative'
  | 'callout'
  | 'record'
  | 'stat'
  | 'data-view'
  | 'checklist'
  | 'timeline'
  | 'quote'
  | 'compare'
  | 'cluster'
  | 'funnel'
  | 'decision';

type ReportCondition = {
  key: string;
  op: 'is' | 'isNot';
  value: string;
};

type ReportSection = {
  id: string;
  title: string;
  kind: ReportSectionKind;
  instruction: string;
  when: ReportCondition | null;
  fallback: string;
};

const REPORT_FENCE = 'agent:report';

const KINDS: ReportSectionKind[] = [
  'custom',
  'narrative',
  'callout',
  'record',
  'stat',
  'data-view',
  'checklist',
  'timeline',
  'quote',
  'compare',
  'cluster',
  'funnel',
  'decision',
];

const newSection = (kind: ReportSectionKind, title: string): ReportSection => ({
  id: uid('sec'),
  title,
  kind,
  instruction: '',
  when: null,
  fallback: '',
});

const conditionText = (when: ReportCondition): string =>
  `${when.key} ${when.op === 'is' ? 'is' : 'is not'} ${when.value}`;

// Headings a reader would expect, not the builder's names for the kinds: an untitled section is still
// titled in the report the agent writes.
const FALLBACK_TITLES: Record<ReportSectionKind, string> = {
  custom: 'Details',
  narrative: 'Summary',
  callout: 'Note',
  record: 'Subject',
  stat: 'Key figures',
  'data-view': 'Findings',
  checklist: 'Checks',
  timeline: 'Timeline',
  quote: 'Evidence',
  compare: 'Comparison',
  cluster: 'Connections',
  funnel: 'Breakdown',
  decision: 'Recommendation',
};

// What an untitled section was headed before FALLBACK_TITLES: a spec saved then still reads as its
// sections, and its next save rewrites the headings.
const LEGACY_FALLBACK_TITLES: Record<ReportSectionKind, string> = {
  ...FALLBACK_TITLES,
  custom: 'Custom format',
  narrative: 'Narrative',
  callout: 'Callout',
  record: 'Record',
  checklist: 'Checklist',
  funnel: 'Funnel',
  decision: 'Decision',
};

const sectionTitle = (section: ReportSection, titles = FALLBACK_TITLES): string =>
  section.title.trim() || titles[section.kind];

const sectionHeading = (section: ReportSection, position: number, titles = FALLBACK_TITLES): string =>
  `${position}. ${sectionTitle(section, titles)}`;

const renderAs = (kind: ReportSectionKind): string =>
  kind === 'narrative'
    ? '_Write this section as prose._'
    : `_Render this section as a \`footprint:${kind}\` smart block._`;

const renderSection = (section: ReportSection, heading: string): string => {
  const lines = [`## ${heading}`];
  if (section.when) {
    lines.push(`_Include only when ${conditionText(section.when)}._`);
    if (section.fallback.trim()) lines.push(`_Otherwise write: ${section.fallback.trim()}_`);
  }
  if (section.kind !== 'custom') lines.push(renderAs(section.kind));
  if (section.instruction.trim()) lines.push(section.instruction.trim());
  return lines.join('\n\n');
};

const serializeWith = (sections: ReportSection[], titles: Record<ReportSectionKind, string>): string => {
  const fence = [`\`\`\`${REPORT_FENCE}`, JSON.stringify({ sections }), '```'].join('\n');
  const prose = sections
    .map((section, index) => renderSection(section, sectionHeading(section, index + 1, titles)))
    .join('\n\n');
  return prose ? `${fence}\n\n${prose}\n` : `${fence}\n`;
};

const serializeOutline = (sections: ReportSection[]): string => serializeWith(sections, FALLBACK_TITLES);

const asCondition = (value: unknown): ReportCondition | null => {
  if (!value || typeof value !== 'object') return null;
  const { key, op, value: right } = value as Record<string, unknown>;
  if (typeof key !== 'string' || typeof right !== 'string') return null;
  return { key, op: op === 'isNot' ? 'isNot' : 'is', value: right };
};

const asSection = (value: unknown, index: number): ReportSection | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const kind = KINDS.includes(raw.kind as ReportSectionKind) ? (raw.kind as ReportSectionKind) : 'narrative';
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : `sec_${index}`,
    title: typeof raw.title === 'string' ? raw.title : '',
    kind,
    instruction: typeof raw.instruction === 'string' ? raw.instruction : '',
    when: asCondition(raw.when),
    fallback: typeof raw.fallback === 'string' ? raw.fallback : '',
  };
};

const reportFence = () => /^```agent:report[ \t]*\r?\n([\s\S]*?)^```[ \t]*$/m;

const parseOutline = (spec: string): ReportSection[] | null => {
  const fence = reportFence().exec(spec);
  if (!fence) return null;
  try {
    const parsed = JSON.parse(fence[1]) as { sections?: unknown };
    if (!Array.isArray(parsed.sections)) return null;
    const sections = parsed.sections.map(asSection);
    const valid = sections.filter(section => section !== null);
    if (valid.length !== sections.length || new Set(valid.map(section => section.id)).size !== valid.length)
      return null;
    return valid;
  } catch {
    return null;
  }
};

const outlineOf = (spec: string): ReportSection[] => {
  const outline = parseOutline(spec);
  // Raw prompt edits must survive even when they no longer match the saved outline.
  if (
    outline &&
    [FALLBACK_TITLES, LEGACY_FALLBACK_TITLES].some(titles => spec.trim() === serializeWith(outline, titles).trim())
  )
    return outline;
  const instruction = spec.trim();
  return instruction ? [{ id: 'sec_prose', title: '', kind: 'custom', instruction, when: null, fallback: '' }] : [];
};

export type { ReportCondition, ReportSection, ReportSectionKind };
export { conditionText, newSection, outlineOf, parseOutline, sectionHeading, sectionTitle, serializeOutline };
